/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jun 22 2023
 **/

import { useRef, PointerEvent } from "react";

type DraggingEvents = {
	onCoordinateChanged?(coordinate: { x: number; y: number }): void;
};

export default function useDragging(
	initialCoordinate?: { x: number; y: number },
	direction: { x: "left" | "right"; y: "top" | "bottom" } = { x: "left", y: "top" },
	events?: DraggingEvents
) {
	const isDragging = useRef(false);
	const startCoordinate = useRef<{ x: number; y: number }>(initialCoordinate ?? { x: 0, y: 0 });
	const coordinate = useRef<{ x: number; y: number }>(initialCoordinate ?? { x: 0, y: 0 });
	const cursor = useRef("default");

	const checkOverFlow = (x: number, y: number, ev: PointerEvent) => {
		const target = ev.currentTarget as HTMLElement;

		const overflowStatus = { overflowX: false, overflowY: false };
		if (x + target.clientWidth > document.body.clientWidth || x < 0) {
			overflowStatus.overflowX = true;
		}
		if (y + target.clientHeight > document.body.clientHeight || y < 60) {
			overflowStatus.overflowY = true;
		}
		return overflowStatus;
	};

	const onPointerDown = (ev: PointerEvent) => {
		const target = ev.target as HTMLElement;
		cursor.current = target.style.cursor;
		target.style.cursor = "grab";
		isDragging.current = true;
		startCoordinate.current = { x: ev.clientX, y: ev.clientY };
		target.setPointerCapture(ev.pointerId);
	};

	const onPointerMove = (ev: PointerEvent) => {
		const target = ev.currentTarget as HTMLDivElement;
		if (!isDragging.current || !target) return;

		target.style.cursor = "grab";

		let nextX = coordinate.current.x;
		let nextY = coordinate.current.y;
		let nextStartX = ev.clientX;
		let nextStartY = ev.clientY;

		if (direction.x === "left") {
			nextX += ev.clientX - startCoordinate.current.x;
		}
		if (direction.x === "right") {
			nextX -= ev.clientX - startCoordinate.current.x;
		}
		if (direction.y === "top") {
			nextY += ev.clientY - startCoordinate.current.y;
		}
		if (direction.y === "bottom") {
			nextY -= ev.clientY - startCoordinate.current.y;
		}
		const { overflowX, overflowY } = checkOverFlow(nextX, nextY, ev);

		if (overflowX) {
			nextX = nextX <= 0 ? 0 : window.innerWidth - target.offsetWidth;
			nextStartX = startCoordinate.current.x;
		}

		if (overflowY) {
			nextY = nextY <= 60 ? 60 : window.innerHeight - target.offsetHeight;
			nextStartY = startCoordinate.current.y;
		}

		coordinate.current = {
			x: nextX,
			y: nextY
		};
		target.style[direction.y] = `${nextY}px`;
		target.style[direction.x] = `${nextX}px`;

		startCoordinate.current = { x: nextStartX, y: nextStartY };
		ev.stopPropagation();
		events?.onCoordinateChanged?.(coordinate.current);
	};

	const onPointerUp = (ev: PointerEvent) => {
		if (!isDragging.current) return;
		const target = ev.target as HTMLElement;
		target.style.cursor = cursor.current;
		isDragging.current = false;
		target.releasePointerCapture(ev.pointerId);
	};

	const onPointerLeave = (ev: PointerEvent) => {
		if (!isDragging.current) return;
		const target = ev.target as HTMLElement;
		target.style.cursor = cursor.current;
		isDragging.current = false;
		target.releasePointerCapture(ev.pointerId);
	};

	return {
		listeners: {
			onPointerDown,
			onPointerMove,
			onPointerLeave,
			onPointerUp
		},
		coordinate: coordinate.current
	};
}
